import {Trans} from '@lingui/macro';
import produce, {castDraft} from 'immer';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CustomFieldResourceTypesEnum, CustomFieldsEnum} from '../../../../models/api/customFieldModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {
  AccountCustomAttributeCondition,
  CustomAttributeCondition,
  RuleCondition,
  RuleConditionTypesEnum,
} from '../../ruleConditionModel';
import {RuleConditionDefinition} from '../../ruleParameterModel';
import {useCustomFields} from '../hooks/useCustomFields';
import {CustomFieldParameter} from '../parameters/customFieldParameter';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {RuleParameterPicker} from '../ruleParameterPicker';

/*
 * Types.
 */

interface RuleCustomAttributeConditionCardProps {
  condition: CustomAttributeCondition | AccountCustomAttributeCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  customFieldResourceType: CustomFieldResourceTypesEnum;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  onUpdate: HandlerOf<RuleCondition>;
  onDelete?: HandlerOf<RuleCondition>;
}

/*
 * Constants.
 */

const CustomFieldParameterName = 'custom_field';

/*
 * Styles.
 */

const CustomFieldContent = styled.div`
  display: grid;
  grid-template-columns: 30% auto;
  grid-column-gap: 5px;
  grid-template-rows: 30px;
`;

/*
 * Component.
 */

export const RuleCustomFieldConditionCard: FC<RuleCustomAttributeConditionCardProps> = (props) => {
  const {condition, conditionDefinition, customFieldResourceType, errorMessage, onUpdate, onDelete} = props;

  const customFields = useCustomFields(customFieldResourceType);

  // This should not happen, but safety check.
  const errorLabel = <Trans id="definition-condition-not-found">Condition {condition.type} not found</Trans>;
  if (!conditionDefinition) {
    return <RuleMultilineParameterCard icon={<RuleConditionIcon id={condition.type} />} title={errorLabel} />;
  }

  const customField = condition.params?.[CustomFieldParameterName] ?? null;

  const onCustomFieldChange = (newCustomFieldId: number) => {
    onUpdate(
      produce(condition, (draft: CustomAttributeCondition | AccountCustomAttributeCondition) => {
        // Erase any left-over params from a previous edit.
        castDraft(draft).params = {custom_field: newCustomFieldId};
        const newCustomFieldModel = customFields.find((field) => field.id === newCustomFieldId);

        if (!newCustomFieldModel) {
          return;
        }

        // Also set the condition type.
        castDraft(draft).type = customFieldTypeToConditionType(
          newCustomFieldModel.type,
          customFieldResourceType,
        );
      }),
    );
  };

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        {getFieldText(customFieldResourceType)}
      </MultilineRuleParameterCardDefaultTitleStyled>
      <CustomFieldParameter
        selectedItem={customField}
        customFieldResourceType={customFieldResourceType}
        onCustomFieldChange={onCustomFieldChange}
        isDisabled={props.isDisabled}
      />
    </>
  );

  const onCancel = onDelete && (() => onDelete(condition));

  // Generic rendering is used for the leftover parameters.
  const customFieldParameters = Object.keys(conditionDefinition.parameters)
    .filter((parameterName) => parameterName !== CustomFieldParameterName)
    .map((parameterName) => {
      const parameter = conditionDefinition.parameters[parameterName];
      const resolvedParameter = conditionDefinition.resolvedParameters[parameterName];
      if (!parameter) {
        return undefined;
      }

      return (
        <RuleParameterPicker
          key={parameter.id || parameterName}
          parameter={parameter}
          resolvedParameter={resolvedParameter}
          parameterName={parameterName}
          customFieldResourceType={customFieldResourceType}
          conditionOrAction={condition}
          cardType={RuleDefaultCardTypesEnum.CONDITION}
          onUpdate={onUpdate}
          isDisabled={props.isDisabled}
        />
      );
    })
    .filter(isDefined);

  return (
    <RuleMultilineParameterCard
      icon={<RuleConditionIcon id={condition.type} />}
      title={title}
      errorMessage={errorMessage}
      onCancel={onCancel}
    >
      {customField && <CustomFieldContent>{customFieldParameters}</CustomFieldContent>}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function customFieldTypeToConditionType(type: CustomFieldsEnum, resourceType: CustomFieldResourceTypesEnum) {
  const isAccount = isAccountResource(resourceType);
  switch (type) {
    case CustomFieldsEnum.NUMBER:
      return isAccount
        ? RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_NUMBER
        : RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_NUMBER;

    case CustomFieldsEnum.BOOLEAN:
      return isAccount
        ? RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_BOOLEAN
        : RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_BOOLEAN;

    case CustomFieldsEnum.DATETIME:
      return isAccount
        ? RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_DATETIME
        : RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_DATETIME;

    case CustomFieldsEnum.TEAMMATE:
      return isAccount
        ? RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_TEAMMATE
        : RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_TEAMMATE;

    case CustomFieldsEnum.STRING:
    default:
      return isAccount
        ? RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_STRING
        : RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_STRING;
  }
}

function isAccountResource(resourceType: CustomFieldResourceTypesEnum) {
  return resourceType === CustomFieldResourceTypesEnum.ACCOUNT;
}

function getFieldText(resourceType: CustomFieldResourceTypesEnum) {
  if (isAccountResource(resourceType)) {
    return <Trans id="custom-account-field">Custom account field</Trans>;
  }
  return <Trans id="custom-contact-field">Custom contact field</Trans>;
}
